import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import makeupcreditService from '@/services/makeupCredit';
import { lessonService } from '@/services/lesson';
import { termService } from '@/services/term';
import type { Lesson, Term } from '@court-sync/shared';
import { toast } from 'react-hot-toast';
import { filterCurrentOrFutureTerms } from './termPickerUtils';

export interface ManualGrantCreditModalProps {
  isOpen: boolean;
  onClose: () => void;
  academyId: string;
  studentId: string;
  onGranted: () => void;
}

export function ManualGrantCreditModal({
  isOpen,
  onClose,
  academyId,
  studentId,
  onGranted,
}: ManualGrantCreditModalProps) {
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [terms, setTerms] = useState<Term[]>([]);
  const [lessonId, setLessonId] = useState('');
  const [termId, setTermId] = useState('');
  const [scope, setScope] = useState<'session' | 'term'>('session');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (!isOpen || !academyId) {
      return undefined;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const [lessonRes, termRes] = await Promise.all([
          lessonService.getLessons({ academyId, activeStatus: 'active' }),
          termService.getTerms(academyId),
        ]);
        if (!cancelled && lessonRes.success && lessonRes.data) setLessons(lessonRes.data);
        if (!cancelled && termRes.success && termRes.data) {
          setTerms(filterCurrentOrFutureTerms(termRes.data));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, academyId]);

  if (!isOpen) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (scope === 'term' && !termId) {
      toast.error('Select a term for a term-scoped credit');
      return;
    }
    setSaving(true);
    try {
      await makeupcreditService.manualGrant({
        academyId,
        studentId,
        lessonId: lessonId || undefined,
        scope,
        ...(scope === 'term' ? { termId } : {}),
        reason: reason.trim() || undefined,
      });
      onGranted();
      onClose();
      setReason('');
      setLessonId('');
      setTermId('');
      setScope('session');
    } catch (err: unknown) {
      console.error(err);
      const ax = err as { response?: { data?: { error?: string } }; message?: string };
      toast.error(ax.response?.data?.error || ax.message || 'Failed to grant credit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-950"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Grant make-up credit</h2>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Lesson (optional — for matching rules)</label>
            <select
              value={lessonId}
              onChange={(e) => setLessonId(e.target.value)}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              disabled={loading}
            >
              <option value="">Default (group)</option>
              {lessons.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Scope</label>
            <select
              value={scope}
              onChange={(e) => {
                const v = e.target.value as 'session' | 'term';
                setScope(v);
                if (v === 'session') setTermId('');
              }}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            >
              <option value="session">Session</option>
              <option value="term">Term (re-enrolment)</option>
            </select>
          </div>
          {scope === 'term' ? (
            <div>
              <label className="mb-1 block text-sm font-medium">Term</label>
              <select
                value={termId}
                onChange={(e) => setTermId(e.target.value)}
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                required
                disabled={loading}
              >
                <option value="">Select term…</option>
                {terms.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                Only current and future terms are listed. The credit can be used for re-enrolment in that term only.
              </p>
            </div>
          ) : null}
          <div>
            <label className="mb-1 block text-sm font-medium">Reason (audit)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Granting…' : 'Grant credit'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
